/**
 * Mapping utilities for converting property_recommendations into
 * service_issues or service_visits. Keeps Opportunities page and
 * OptimizationInsightsCard free of duplicate logic.
 */

interface RecommendationContext {
  id: string;
  property_id: string;
  property_name: string;
  recommendation_type: string;
  priority: string;
  annual_waste_dollars: number | null;
  annual_avoidable_import_dollars: number | null;
  estimated_annual_savings: number | null;
  estimated_cost: number | null;
  estimated_payback_years: number | null;
  confidence: string;
  evaluated_at: string;
}

const TYPE_LABELS: Record<string, string> = {
  battery_expansion: 'Battery Expansion',
  pv_addition: 'Solar Addition',
  tou_shift: 'TOU Optimization',
  load_management: 'Load Management',
};

export function mapRecommendationToIssueType(recType: string): string {
  switch (recType) {
    case 'battery_expansion': return 'upgrade_recommendation';
    case 'pv_addition': return 'upgrade_recommendation';
    case 'tou_shift': return 'configuration_recommendation';
    case 'load_management': return 'configuration_recommendation';
    default: return 'upgrade_recommendation';
  }
}

export function mapRecommendationToSeverity(priority: string): string {
  switch (priority) {
    case 'high': return 'high';
    case 'medium': return 'medium';
    case 'low': return 'low';
    default: return 'info';
  }
}

export function mapRecommendationToVisitType(recType: string): string {
  switch (recType) {
    case 'battery_expansion': return 'preventive';
    case 'pv_addition': return 'preventive';
    case 'tou_shift': return 'preventive';
    case 'load_management': return 'preventive';
    default: return 'preventive';
  }
}

export function buildRecommendationIssueTitle(rec: RecommendationContext): string {
  const label = TYPE_LABELS[rec.recommendation_type] ?? rec.recommendation_type;
  return `${label} recommendation — ${rec.property_name}`;
}

export function buildRecommendationIssueDescription(rec: RecommendationContext): string {
  const parts: string[] = [];
  const label = TYPE_LABELS[rec.recommendation_type] ?? rec.recommendation_type;
  parts.push(`${label} opportunity identified for ${rec.property_name}.`);

  if (rec.annual_waste_dollars != null && rec.annual_waste_dollars > 0) {
    parts.push(`Estimated annual waste: $${rec.annual_waste_dollars.toFixed(0)}.`);
  }
  if (rec.estimated_annual_savings != null && rec.estimated_annual_savings > 0) {
    parts.push(`Potential annual savings: $${rec.estimated_annual_savings.toFixed(0)}.`);
  }
  if (rec.estimated_cost != null && rec.estimated_cost > 0) {
    parts.push(`Estimated cost: $${rec.estimated_cost.toLocaleString()}.`);
  }
  if (rec.estimated_payback_years != null) {
    parts.push(`Estimated payback: ${rec.estimated_payback_years} year${rec.estimated_payback_years !== 1 ? 's' : ''}.`);
  }
  parts.push(`Confidence: ${rec.confidence}. Evaluated: ${new Date(rec.evaluated_at).toLocaleDateString()}.`);

  return parts.join(' ');
}

export function buildRecommendationTriggerSnapshot(rec: RecommendationContext): Record<string, unknown> {
  return {
    source: 'recommendation',
    recommendation_id: rec.id,
    recommendation_type: rec.recommendation_type,
    priority: rec.priority,
    annual_waste_dollars: rec.annual_waste_dollars,
    annual_avoidable_import_dollars: rec.annual_avoidable_import_dollars,
    estimated_annual_savings: rec.estimated_annual_savings,
    estimated_cost: rec.estimated_cost,
    estimated_payback_years: rec.estimated_payback_years,
    confidence: rec.confidence,
    evaluated_at: rec.evaluated_at,
  };
}

export function buildVisitInternalNotes(rec: RecommendationContext): string {
  const parts: string[] = [];
  const label = TYPE_LABELS[rec.recommendation_type] ?? rec.recommendation_type;
  parts.push(`Visit from ${label} recommendation.`);

  if (rec.annual_waste_dollars != null && rec.annual_waste_dollars > 0) {
    parts.push(`Annual waste: $${rec.annual_waste_dollars.toFixed(0)}.`);
  }
  if (rec.estimated_annual_savings != null && rec.estimated_annual_savings > 0) {
    parts.push(`Potential savings: $${rec.estimated_annual_savings.toFixed(0)}/yr.`);
  }
  if (rec.estimated_cost != null && rec.estimated_cost > 0) {
    parts.push(`Est. cost: $${rec.estimated_cost.toLocaleString()}.`);
  }
  if (rec.estimated_payback_years != null) {
    parts.push(`Payback: ${rec.estimated_payback_years} yr.`);
  }
  parts.push(`Confidence: ${rec.confidence}.`);

  return parts.join(' ');
}
